import React from 'react';
import { Info } from 'lucide-react';

export const RECOVERY_DEFAULTS = {
  enabled: false,
  primary_model: 'claude-sonnet-5-5',
  recovery_model: 'claude-opus-5-5',
  deadline_seconds: 90,
  max_cost_usd: 0.5,
  max_input_tokens: 80000,
  primary_output_tokens: 6000,
  recovery_output_tokens: 8000,
};

const MODES = [
  ['claude-haiku-5-5', 'claude-sonnet-5-5', 'Haiku → Sonnet'],
  ['claude-haiku-5-5', 'claude-opus-5-5', 'Haiku → Opus'],
  ['claude-sonnet-5-5', 'claude-opus-5-5', 'Sonnet → Opus'],
];
const LIMITS = [
  ['deadline_seconds', 'Time limit per receipt (seconds)', 1, 180, 'any'],
  ['max_cost_usd', 'Cost budget per receipt (USD)', 0, 5, 'any'],
  ['max_input_tokens', 'Maximum input tokens per attempt', 1000, 100000, 1],
  ['primary_output_tokens', 'Maximum primary output tokens', 256, 16000, 1],
  ['recovery_output_tokens', 'Maximum recovery output tokens', 256, 16000, 1],
];
export const recoveryModeLabel = value => MODES.find(([primary, recovery]) => primary === value.primary_model && recovery === value.recovery_model)?.[2] || 'Unsupported mode';
export const recoveryPolicy = value => ({ ...RECOVERY_DEFAULTS, ...value });
export const recoveryError = value => {
  if (!MODES.some(([primary, recovery]) => primary === value.primary_model && recovery === value.recovery_model)) {
    return 'Select a supported parsing mode.';
  }
  for (const [key, label, min, max] of LIMITS) {
    const number = Number(value[key]);
    if (value[key] === '' || !Number.isFinite(number) || number < min || number > max || (key === 'max_cost_usd' && number === 0) ||
        (key.endsWith('_tokens') && !Number.isInteger(number))) {
      return `${label} must be ${key === 'max_cost_usd' ? 'greater than 0 and at most 5' : `${min}–${max}`}${key.endsWith('_tokens') ? ' whole tokens' : ''}.`;
    }
  }
  return '';
};
export const serializeRecovery = value => ({
  ...value,
  ...Object.fromEntries(LIMITS.map(([key]) => [key, Number(value[key])])),
});

const MODE_HINTS = {
  'Haiku → Sonnet': 'Fastest primary, mid-tier recovery',
  'Haiku → Opus': 'Evaluation only: accuracy and cost not established',
  'Sonnet → Opus': 'Default',
};
const limitHint = (key, min, max) => key === 'max_cost_usd'
  ? 'USD, greater than 0 and at most 5'
  : `${min.toLocaleString()}–${max.toLocaleString()}${key.endsWith('_tokens') ? ' whole tokens' : ' seconds'}`;
const fieldClass = 'h-10 w-full rounded-lg border border-ui-control bg-ui-surface px-3 text-sm text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-primary/40 focus:border-ui-primary disabled:opacity-60';

export default function ParsingRecoverySettings({ value, onChange, saving }) {
  const mode = MODES.findIndex(([primary, recovery]) => primary === value.primary_model && recovery === value.recovery_model);
  const error = recoveryError(value);
  return (
    <section className="overflow-hidden rounded-2xl border border-ui-line bg-ui-surface shadow-ui-card" aria-labelledby="parsing-recovery-heading">
      <div className="flex items-start justify-between gap-4 border-b border-ui-divider px-5 py-5 sm:px-[22px]">
        <div>
          <h2 id="parsing-recovery-heading" className="text-[17px] font-bold text-ui-ink">AI parsing recovery</h2>
          <p className="mt-1 max-w-[62ch] text-[13px] leading-relaxed text-ui-muted">Use source validation and at most one stronger-model recovery attempt for email bodies and travel documents.</p>
        </div>
        <label className="flex flex-none cursor-pointer items-center gap-2.5 text-[13px] font-semibold text-ui-ink">
          {value.enabled ? 'On' : 'Off'}
          <input type="checkbox" role="switch" aria-label="Enable AI parsing recovery" className="peer sr-only"
            checked={value.enabled} disabled={saving}
            onChange={e => onChange({ ...value, enabled: e.target.checked })} />
          <span aria-hidden="true" className="relative h-6 w-10 rounded-full bg-ui-control transition-colors peer-checked:bg-ui-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ui-primary/40 peer-disabled:opacity-50 after:absolute after:left-[3px] after:top-[3px] after:h-[18px] after:w-[18px] after:rounded-full after:bg-ui-surface after:shadow-ui-card after:transition-transform peer-checked:after:translate-x-4" />
        </label>
      </div>

      <div className="space-y-5 px-5 py-5 sm:px-[22px]">
        <p className="text-[13px] text-ui-ink-3" role="status">
          {value.enabled
            ? `Recovery policy selected: ${MODES[mode]?.[2] || 'unsupported mode'}. Takes effect after Save Changes.`
            : 'Recovery is off. The standard parsing model and fallback settings apply.'}
        </p>

        <fieldset aria-describedby="parsing-recovery-mode-hint" disabled={saving}>
          <legend className="mb-2.5 text-[13px] font-semibold text-ui-ink-2">Parsing recovery mode</legend>
          {mode < 0 && (
            <p className="mb-2.5 rounded-[10px] border border-ui-amber-line bg-ui-amber-bg px-3.5 py-2.5 text-[13px] text-ui-amber-fg">
              The saved mode isn't supported. Choose a supported mode.
            </p>
          )}
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            {MODES.map(([primary_model, recovery_model, label], index) => (
              <label
                key={label}
                className={`flex cursor-pointer items-start gap-2.5 rounded-[10px] border px-3.5 py-3 ${index === mode ? 'border-ui-primary bg-ui-blue-bg' : 'border-ui-control bg-ui-surface hover:bg-ui-hover'} ${saving ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <input
                  type="radio"
                  name="parsing-recovery-mode"
                  className="mt-0.5 accent-[rgb(var(--ui-primary))]"
                  checked={index === mode}
                  onChange={() => onChange({ ...value, primary_model, recovery_model })}
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-ui-ink">{label}</span>
                  {MODE_HINTS[label] && <span className="text-xs text-ui-muted">{MODE_HINTS[label]}</span>}
                </span>
              </label>
            ))}
          </div>
          <p id="parsing-recovery-mode-hint" className="mt-2 text-xs text-ui-muted">Selecting a mode does not turn recovery on.</p>
        </fieldset>

        <div>
          <h3 className="mb-3 text-[13px] font-semibold text-ui-ink-2">Cost, time and token limits</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {LIMITS.map(([key, label, min, max, step]) => (
              <div key={key} className="flex flex-col gap-1.5">
                <label htmlFor={`recovery-${key}`} className="text-[13px] font-semibold text-ui-ink-2">{label}</label>
                <input id={`recovery-${key}`} aria-describedby={`recovery-${key}-hint`} type="number" min={min} max={max} step={step}
                  value={value[key]} disabled={saving} className={fieldClass}
                  onChange={e => onChange({ ...value, [key]: e.target.value })} />
                <span id={`recovery-${key}-hint`} className="text-xs text-ui-muted">{limitHint(key, min, max)}</span>
              </div>
            ))}
          </div>
        </div>

        {error && <p role="alert" className="rounded-lg border border-ui-red-line bg-ui-red-bg px-3.5 py-2.5 text-[13px] text-ui-red-fg">{error}</p>}

        <div className="flex items-start gap-2.5 rounded-lg border border-ui-blue-line bg-ui-blue-bg px-3.5 py-3 text-[13px] leading-relaxed text-ui-blue-fg">
          <Info className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
          <div className="space-y-1.5">
            <p>The time and conservative model-cost budgets are shared across the email and its attachments. Long sources and schedule changes may go directly to the recovery model. Scans and unverified results go to review.</p>
            <p>This is a global setting. AI parsing must also be enabled in Email Parsing settings. AwardWallet settings are managed separately. Standard fallback and retry controls below apply when recovery is off.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
